import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Package, ShoppingCart, Calculator,
  Scale, History, ReceiptText, Users, LogOut, Leaf, Menu, Tag, Crown, Percent, UserCircle2, Coins, ArrowLeftRight
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import StoreSwitcher from "./StoreSwitcher";
import ActiveStoreBanner from "./ActiveStoreBanner";
import { StoreProvider, useStore } from "@/context/StoreContext";

function StoreScopedOutlet() {
  const { loading, active, multiTenant, error } = useStore();
  if (loading) return <div className="p-10 text-muted-foreground" data-testid="store-loading">Loading store…</div>;
  if (multiTenant && !active) {
    return <div className="p-10 text-destructive" data-testid="store-unavailable">{error || "You have no access to any active store. Contact your administrator."}</div>;
  }
  // Remount every page on store change so no state/cart/data crosses stores.
  return (
    <>
      <ActiveStoreBanner />
      <Outlet key={active ? `${active.tenant_id}:${active.store_id}` : "single"} />
    </>
  );
}

const ELEVATED = ["platform_owner", "vendor_owner", "admin", "manager"];
const ELEVATED_ADMIN = ["platform_owner", "vendor_owner", "admin"];

const nav = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview", roles: ["platform_owner","vendor_owner","admin","manager","cashier"], testid: "nav-dashboard" },
  { to: "/pos", icon: ShoppingCart, label: "POS", roles: ["platform_owner","vendor_owner","admin","manager","cashier"], testid: "nav-pos" },
  { to: "/inventory", icon: Package, label: "Inventory", roles: ["platform_owner","vendor_owner","admin","manager","cashier"], testid: "nav-inventory" },
  { to: "/transfers", icon: ArrowLeftRight, label: "Stock Transfers", roles: ELEVATED, testid: "nav-transfers" },
  { to: "/pricing", icon: Calculator, label: "Pricing Engine", roles: ELEVATED, testid: "nav-pricing" },
  { to: "/deals", icon: Tag, label: "Specials & Deals", roles: ELEVATED, testid: "nav-deals" },
  { to: "/discount-requests", icon: Percent, label: "Discount approvals", roles: ELEVATED, testid: "nav-discount-requests" },
  { to: "/sales", icon: ReceiptText, label: "Sales History", roles: ["platform_owner","vendor_owner","admin","manager","cashier"], testid: "nav-sales" },
  { to: "/customers", icon: UserCircle2, label: "Members", roles: ["platform_owner","vendor_owner","admin","manager","cashier"], testid: "nav-customers" },
  { to: "/loyalty", icon: Coins, label: "Loyalty", roles: ELEVATED_ADMIN, testid: "nav-loyalty" },
  { to: "/reconcile", icon: Scale, label: "Reconciliation", roles: ELEVATED, testid: "nav-reconcile" },
  { to: "/movements", icon: History, label: "Movement Log", roles: ["platform_owner","vendor_owner","admin","manager","cashier"], testid: "nav-movements" },
  { to: "/users", icon: Users, label: "Users", roles: ELEVATED_ADMIN, testid: "nav-users" },
];

const ROLE_LABEL = {
  platform_owner: "PLATFORM OWNER",
  vendor_owner: "OWNER",
  admin: "ADMIN",
  manager: "MANAGER",
  cashier: "CASHIER",
};
const ROLE_TONE = {
  platform_owner: "bg-primary text-primary-foreground hover:bg-primary",
  vendor_owner: "bg-secondary text-secondary-foreground hover:bg-secondary",
  admin: "bg-slate-800 text-white hover:bg-slate-800",
  manager: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  cashier: "bg-slate-200 text-slate-700 hover:bg-slate-200",
};

function NavList({ items, onNavigate, testidPrefix = "" }) {
  return (
    <div className="space-y-1">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          onClick={onNavigate}
          data-testid={`${testidPrefix}${it.testid}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 ${
              isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
            }`
          }
        >
          <it.icon className="w-4 h-4" strokeWidth={1.5} />
          {it.label}
        </NavLink>
      ))}
    </div>
  );
}

function BrandBlock() {
  return (
    <div className="flex items-center gap-2">
      <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground grid place-items-center">
        <Leaf className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <div>
        <div className="font-display text-lg leading-none">Saint Herb</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">KhulaTill POS</div>
      </div>
    </div>
  );
}

function UserBlock({ user, onLogout, testidPrefix = "" }) {
  const isOwner = user.role === "platform_owner" || user.role === "vendor_owner";
  return (
    <div className="flex items-center gap-3 px-2 py-2 border-t border-border">
      <Avatar className="h-8 w-8">
        <AvatarImage src={user.picture} />
        <AvatarFallback>{user.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate flex items-center gap-1" data-testid={`${testidPrefix}current-user-name`}>
          {user.name}
          {isOwner && <Crown className="w-3 h-3 text-primary" strokeWidth={1.5} />}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          <Badge className={`text-[10px] ${ROLE_TONE[user.role] || ROLE_TONE.cashier}`} data-testid={`${testidPrefix}current-user-role`}>
            {ROLE_LABEL[user.role] || user.role?.toUpperCase()}
          </Badge>
        </div>
      </div>
      <button
        data-testid={`${testidPrefix}logout-btn`}
        onClick={onLogout}
        className="p-1.5 rounded hover:bg-muted text-muted-foreground"
        title="Sign out"
      >
        <LogOut className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}

export default function Layout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  }

  const items = nav.filter(n => n.roles.includes(user.role));
  const current = items.find(n => n.to === location.pathname);

  return (
    <StoreProvider>
    <div className="min-h-screen flex bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-border bg-white">
        <div className="px-6 py-6">
          <BrandBlock />
        </div>
        <nav className="px-3 flex-1">
          <div className="mb-3 px-1">
            <StoreSwitcher />
          </div>
          <NavList items={items} />
        </nav>
        <div className="p-3">
          <UserBlock user={user} onLogout={logout} />
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar with hamburger */}
        <header className="md:hidden sticky top-0 z-30 bg-white border-b border-border px-3 py-2.5 flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-menu-btn" aria-label="Open menu">
                <Menu className="w-5 h-5" strokeWidth={1.5} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col">
              <div className="px-6 py-6"><BrandBlock /></div>
              <nav className="px-3 flex-1 overflow-y-auto thin-scroll" data-testid="mobile-nav">
                <div className="mb-3 px-1">
                  <StoreSwitcher testidPrefix="m-" />
                </div>
                <NavList items={items} onNavigate={() => setMobileOpen(false)} testidPrefix="m-" />
              </nav>
              <div className="p-3">
                <UserBlock user={user} onLogout={logout} testidPrefix="mobile-" />
              </div>
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0">
              <Leaf className="w-3.5 h-3.5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-base leading-none truncate">{current?.label || "Saint Herb"}</div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground">KhulaTill POS</div>
            </div>
          </div>
          <Badge className={`text-[10px] shrink-0 ${ROLE_TONE[user.role] || ROLE_TONE.cashier}`} data-testid="mobile-user-role">{ROLE_LABEL[user.role] || user.role?.toUpperCase()}</Badge>
        </header>
        <div className="flex-1 min-w-0">
          <StoreScopedOutlet />
        </div>
      </main>
    </div>
    </StoreProvider>
  );
}
